import { ApprovalCodeSchema, DecisionSchema, type PermissionRequest } from '@gestion-permisos/contracts';
import { type ReactNode, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { ApiProblem } from '../api/problem.js';
import { ApiErrorView } from '../components/api-error-view.js';
import { buttonClasses } from '../components/button-styles.js';
import { Card, CARD_SURFACE, CardContent, CardHeader, CardTitle } from '../components/card.js';
import { Modal, ModalFooter } from '../components/modal.js';
import { PageHeader } from '../components/page-header.js';
import { ProblemMessage } from '../components/problem-message.js';
import { RequestDocument } from '../requests/request-document.js';
import { type FieldRow, RequestFields, RequestHistory } from '../requests/request-details.js';
import { useDecideRequest, useRequest, useRequestHistory } from '../requests/requests-api.js';
import { StatusBadge } from '../requests/status-badge.js';
import { useMe } from '../session/session.js';
import { NOT_A_REQUEST } from './request-detail-page.js';

/** Código del enlace de aprobación del correo en el fragmento (`#codigo=…`), si tiene la forma esperada. */
export function approvalCodeFrom(hash: string): string | undefined {
  const parsed = ApprovalCodeSchema.safeParse(new URLSearchParams(hash.replace(/^#/, '')).get('codigo'));
  return parsed.success ? parsed.data : undefined;
}

/**
 * Revisión del aprobador (W4): detalle e historial con los nombres de H-01, y la decisión. La pantalla solo
 * se lee con `GET`; **decidir es siempre un `POST`** desde la sesión. Sin reasignación, duración real ni
 * cancelación de otra persona (W9).
 *
 * **Enlace del correo (D-13):** el aviso al responsable abre esta pantalla con `#codigo=…`. El código se lee
 * una vez, se guarda **solo en memoria** y se quita de la URL (no queda en el historial ni en marcadores).
 * Abrir el enlace no decide nada: el código solo viaja con la decisión, y el servidor registra el canal
 * `EMAIL` si es válido.
 */
export function ApprovalReviewPage() {
  const { id = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [approvalCode] = useState(() => approvalCodeFrom(location.hash));
  useEffect(() => {
    if (location.hash !== '') void navigate({ pathname: location.pathname, search: location.search }, { replace: true });
  }, [location.hash, location.pathname, location.search, navigate]);
  if (!z.uuid().safeParse(id).success) return <ApiErrorView error={NOT_A_REQUEST} />;
  return <ApprovalReview id={id} approvalCode={approvalCode} />;
}

function ApprovalReview({ id, approvalCode }: { id: string; approvalCode: string | undefined }) {
  const request = useRequest(id);
  // El historial se pide a la vez que la solicitud, como antes.
  const history = useRequestHistory(id);

  if (request.isPending) return <output className="block text-muted-foreground">Cargando…</output>;
  if (request.isError) return <ApiErrorView error={request.error} />;
  return <Review request={request.data} history={history} approvalCode={approvalCode} />;
}

const CONFLICT_NOTICE = 'La solicitud cambió mientras la revisabas. Se muestra su estado actual.';
const FROM_EMAIL_NOTICE = 'Abriste esta solicitud desde el enlace del correo. Revísala y confirma tu decisión: abrir el enlace no aprueba ni rechaza nada.';

/** Aviso tras decidir, con el canal que registró el servidor (nunca lo elige la pantalla). */
function decidedNotice(done: string, decided: PermissionRequest, approvalCode: string | undefined): string {
  if (decided.decisionChannel === 'EMAIL') return `${done} Quedó registrada como decidida desde el correo.`;
  if (approvalCode !== undefined) return `${done} El enlace del correo ya no era válido: quedó registrada como decidida desde la aplicación.`;
  return done;
}

/**
 * Revisión con el aspecto del detalle propio y de la Power App (`src/pages/manager-request-review-page.tsx`):
 * cabecera con la insignia y, si se puede decidir, «Aprobar» y «Rechazar» a la derecha; datos, documento e
 * historial en tarjetas. Tras decidir, la pantalla se queda aquí y muestra el aviso.
 *
 * Aprobar o rechazar (`POST /requests/{id}/decision` con `If-Match` e `Idempotency-Key`). Solo se ofrece,
 * como presentación, al aprobador almacenado de una solicitud pendiente: la API decide siempre (403, 409,
 * 412 o 422 se muestran tal cual). El motivo del rechazo se valida con `DecisionSchema` antes de enviarlo.
 *
 * «Aprobar» decide con un clic. «Rechazar» abre una ventana (`Modal`) con el motivo, como en la Power App;
 * cerrarla (Volver, la X o Escape) no llama a la API. Mientras está abierta, sus avisos y errores van dentro.
 */
function Review({
  request,
  history,
  approvalCode,
}: {
  request: PermissionRequest;
  history: ReturnType<typeof useRequestHistory>;
  approvalCode: string | undefined;
}) {
  const me = useMe();
  const decide = useDecideRequest(request.id);
  const [rejecting, setRejecting] = useState(false);
  const [notice, setNotice] = useState<string | undefined>(undefined);

  const canDecide = request.approverId === me.id && request.status === 'PENDING_APPROVAL';
  const error = decide.error;
  const forbidden = error instanceof ApiProblem && error.status === 403;
  // La ventana solo existe mientras se puede decidir: si un 409 o un 412 cambian el estado, o llega un 403,
  // se cierra y el aviso queda en la página.
  const open = canDecide && rejecting && !forbidden;
  const people: FieldRow[] = [
    ['Solicitante', request.employee.fullName],
    ...(request.approver !== null ? ([['Aprobador', request.approver.fullName]] as FieldRow[]) : []),
    ['Tipo de permiso', request.permissionType.name],
  ];

  /** Envía la decisión; devuelve `false`, sin llamar a la API, si no cumple `DecisionSchema` (motivo vacío). */
  const send = (decision: z.input<typeof DecisionSchema>, done: string): boolean => {
    const parsed = DecisionSchema.safeParse(approvalCode === undefined ? decision : { ...decision, approvalCode });
    if (!parsed.success) return false;
    setNotice(undefined);
    decide.mutate(
      { version: request.version, decision: parsed.data },
      {
        onSuccess: (decided) => {
          setRejecting(false);
          setNotice(decidedNotice(done, decided, approvalCode));
        },
        onError: (failure) => {
          if (failure instanceof ApiProblem && (failure.status === 409 || failure.status === 412)) setNotice(CONFLICT_NOTICE);
        },
      },
    );
    return true;
  };

  // Avisos y errores: dentro de la ventana mientras está abierta; si no, en la página. Nunca en los dos sitios.
  const feedback = (
    <>
      {notice ? <output className="block rounded-md border border-border bg-muted p-3 text-sm">{notice}</output> : null}
      {error !== null ? forbidden ? <ApiErrorView error={error} /> : <ProblemMessage error={error} /> : null}
    </>
  );

  // «Aprobar» y «Rechazar» siguen en la cabecera aunque la ventana esté abierta (la ventana los tapa).
  const actions = canDecide ? (
    <>
      <button type="button" disabled={decide.isPending} className={buttonClasses()} onClick={() => send({ decision: 'APPROVE' }, 'Solicitud aprobada.')}>
        Aprobar
      </button>
      <button
        type="button"
        disabled={decide.isPending}
        className={buttonClasses('outline')}
        onClick={() => {
          setNotice(undefined);
          decide.reset();
          setRejecting(true);
        }}
      >
        Rechazar
      </button>
    </>
  ) : null;

  return (
    <div className="space-y-6 lg:px-6 lg:py-4">
      <Link to="/aprobaciones" className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground">
        Volver a aprobaciones
      </Link>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {request.requestNumber} <StatusBadge status={request.status} tokens />
          </span>
        }
        description="Revisa los datos, el documento y el historial antes de decidir."
        action={actions}
      />
      {open ? null : feedback}
      {canDecide && approvalCode !== undefined && notice === undefined ? (
        <p className="rounded-md border border-border bg-accent p-3 text-sm text-accent-foreground">{FROM_EMAIL_NOTICE}</p>
      ) : null}
      {open ? (
        <Modal title="Rechazar solicitud" description="El motivo será visible en la solicitud y en su historial." onClose={() => setRejecting(false)}>
          {/* Sin `key`: tras un 409 o un 412 que no cambian el estado, la ventana y el motivo siguen (misma clave). */}
          <RejectForm
            pending={decide.isPending}
            feedback={feedback}
            onBack={() => setRejecting(false)}
            onReject={(reason) => send({ decision: 'REJECT', reason: reason.trim() === '' ? undefined : reason }, 'Solicitud rechazada.')}
          />
        </Modal>
      ) : null}
      <Card>
        <CardHeader>
          <CardTitle>Datos del permiso</CardTitle>
        </CardHeader>
        <CardContent>
          <RequestFields request={request} leading={people} className="" />
        </CardContent>
      </Card>
      <RequestDocument request={request} editable={false} className={`p-5 ${CARD_SURFACE}`} />
      <Card>
        <CardHeader>
          <CardTitle id="historial">Historial</CardTitle>
        </CardHeader>
        <CardContent>
          {history.isPending ? <output className="block text-muted-foreground">Cargando el historial…</output> : null}
          {history.isError ? <ApiErrorView error={history.error} /> : null}
          {history.isSuccess ? <RequestHistory events={history.data} showNames tokens /> : null}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Motivo del rechazo, obligatorio (lo valida `DecisionSchema` antes de enviar). Se monta con la ventana: al
 * reabrirla empieza vacío.
 */
function RejectForm({
  pending,
  feedback,
  onBack,
  onReject,
}: {
  pending: boolean;
  feedback: ReactNode;
  onBack: () => void;
  /** `false` si el motivo no es válido y no se llamó a la API. */
  onReject: (reason: string) => boolean;
}) {
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | undefined>(undefined);
  return (
    <form
      noValidate
      aria-label="Rechazar la solicitud"
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        setReasonError(onReject(reason) ? undefined : 'Escribe el motivo del rechazo (hasta 2000 caracteres).');
      }}
    >
      {feedback}
      <div className="space-y-2">
        <label htmlFor="reject-reason" className="text-sm font-medium">
          Motivo del rechazo
        </label>
        <textarea
          id="reject-reason"
          rows={3}
          maxLength={2000}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          aria-invalid={reasonError !== undefined}
          aria-describedby={reasonError !== undefined ? 'reject-reason-error' : undefined}
          className="block w-full rounded-md border border-input bg-card px-2 py-1.5"
        />
        {reasonError ? (
          <p id="reject-reason-error" className="text-sm text-destructive">
            {reasonError}
          </p>
        ) : null}
      </div>
      <ModalFooter>
        <button type="button" disabled={pending} className={buttonClasses('outline')} onClick={onBack}>
          Volver
        </button>
        <button type="submit" disabled={pending} className={buttonClasses()}>
          Confirmar rechazo
        </button>
      </ModalFooter>
    </form>
  );
}
